import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPalette, faCheck } from "@fortawesome/free-solid-svg-icons";
import { Themes, Theme } from "../../data/Themes";
import { applyAndPersistTheme } from "../../utils/themeController";
import { showToast } from "../../store/slices/toastSlice";

/**
 * Miniature app preview shown inside each theme card — a tiny window with a
 * side rail, a coloured header bar (the brand accent) and two content lines.
 * Light/dark surfaces mirror the app's real light/dark chrome.
 */
const ThemePreview: React.FC<{ theme: Theme }> = ({ theme }) => {
  const isDark = theme.mode === "dark";
  const surface = isDark ? "#1E2A38" : "#ffffff";
  const rail = isDark ? "#0f1a26" : "#eef2f7";
  const line = isDark ? "#33445a" : "#e5e7eb";

  return (
    <div
      className="flex gap-2 rounded-lg border p-3 h-28"
      style={{ backgroundColor: surface, borderColor: isDark ? "#33445a" : "#e5e7eb" }}
    >
      <div className="w-2.5 rounded" style={{ backgroundColor: rail }} />
      <div className="flex-1 flex flex-col gap-2 pt-1">
        <div
          className="h-3 w-full rounded"
          style={{ backgroundColor: theme.primaryColor }}
        />
        <div className="h-2 w-3/4 rounded" style={{ backgroundColor: line }} />
        <div className="h-2 w-1/2 rounded" style={{ backgroundColor: line }} />
      </div>
    </div>
  );
};

export const Appearance: React.FC = () => {
  const dispatch = useDispatch();
  const themeName = useSelector((state: any) => state.theme.themeName);
  const appMode = useSelector((state: any) => state.theme.appMode);
  const isDark = appMode === "dark";
  const localization = useSelector(
    (state: any) => state.localization?.localization
  );

  const selectTheme = async (name: string) => {
    if (name === themeName) return;
    await applyAndPersistTheme(dispatch, name);
    const selected = Themes.find((t) => t.name === name);
    dispatch(
      showToast({
        severity: "success",
        summary: localization?.ThemeUpdated || "Theme updated",
        detail: `${selected?.label || ""} ${localization?.Applied || "applied"}`,
      })
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <h1 className="text-xl font-bold surface-900 mb-1">
          {localization?.Appearance || "Appearance"}
        </h1>
      </div>
      <p className="text-sm text-gray-500 mt-0 mb-4">
        {localization?.AppearanceSubtitle ||
          "Personalize how the app looks for you."}
      </p>

      <div className="border shadow-sm rounded-lg bg-white p-4">
        {/* Section header */}
        <div className="flex items-start gap-2.5 mb-4">
          <span
            className="inline-flex items-center justify-center w-8 h-8 rounded-md shrink-0"
            style={{
              backgroundColor: "var(--boxShadow)",
              color: "var(--primaryColor)",
            }}
          >
            <FontAwesomeIcon icon={faPalette} />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-gray-800 m-0">
              {localization?.Theme || "Theme"}
            </h2>
            <p className="text-sm text-gray-500 m-0">
              {localization?.ThemeHelp ||
                "Choose a theme that sets the colors across the whole app."}
            </p>
          </div>
        </div>

        {/* Theme cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Themes.map((theme) => {
            const isActive = themeName === theme.name;
            return (
              <button
                key={theme.name}
                type="button"
                onClick={() => selectTheme(theme.name)}
                aria-pressed={isActive}
                aria-label={theme.label}
                className="relative text-left rounded-lg border-2 p-3 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  backgroundColor: isDark ? "#1E2A38" : "#ffffff",
                  borderColor: isActive
                    ? "var(--primaryColor)"
                    : isDark
                      ? "#33445a"
                      : "#e5e7eb",
                }}
              >
                {isActive && (
                  <span
                    className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full text-white shadow-sm z-10"
                    style={{ backgroundColor: "var(--primaryColor)" }}
                  >
                    <FontAwesomeIcon icon={faCheck} className="text-xs" />
                  </span>
                )}
                <ThemePreview theme={theme} />
                <div className="mt-3">
                  <div
                    className="text-base font-semibold"
                    style={{ color: isDark ? "#F2F2F2" : "#1F2937" }}
                  >
                    {theme.label}
                  </div>
                  <div
                    className="text-sm"
                    style={{ color: isDark ? "#9CA9B8" : "#6B7280" }}
                  >
                    {theme.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Appearance;
